import React, { useContext } from 'react'
import styled from 'styled-components'

import OnlyIfChildren from 'components/Common/OnlyIfChildren'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Pill from 'components/Luxkit/Pill'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import Group from 'components/utils/Group'
import NoPrint from 'components/utils/NoPrint'
import config from 'constants/config'
import { CurrencyProvider } from 'contexts/currencyContext'
import GeoContext from 'contexts/geoContext'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { getPlural } from 'lib/string/pluralize'
import { PriceDisplayType } from 'tripPlanner/types/tripItem'

export type DiscountDisplayType = 'badge' | 'inlineBadge' | 'following' | 'none'

export type Props = {
  depositComponent?: React.ReactNode
  discountDisplayType?: DiscountDisplayType
  discount?: string | number
  price: number
  currencyCode: string
  priceLabel: string
  totalLabel?: string
  roomOccupancy?: string
  priceDisplayType: PriceDisplayType | undefined
  className?: string
}

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const RoomText = styled.div`
  font-size: 11px;
  font-weight: ${(props) => props.theme.font.primary.weight.regular};
  line-height: 16px;
  color: ${({ theme }) => theme.palette.neutral.two};
`

const buildPriceLabel = (min: number, max: number, singular: string) => {
  const nights = min !== max ? `${min} to ${max}` : `${min}`
  const qualifier = getPlural(singular, min)
  return `${nights} ${qualifier} from`
}

export const buildNightsPriceLabel = (nightsMin: number, nightsMax: number) =>
  buildPriceLabel(nightsMin, nightsMax, 'night')

export const buildDaysPriceLabel = (daysMin: number, daysMax: number) =>
  buildPriceLabel(daysMin, daysMax, 'day')

function PriceDetails({
  depositComponent,
  discountDisplayType,
  discount,
  price,
  currencyCode,
  priceLabel,
  totalLabel,
  roomOccupancy,
  priceDisplayType,
  className,
}: Props) {
  const { currentRegionCode } = useContext(GeoContext)
  const currencyRegionCode =
    config.regions.find((region) => region.currencyCode === currencyCode)
      ?.code ?? ''

  const showPriceDisplayType =
    isEnabledForFeature(config.TAX_EXCLUSIVE_REGIONS, currencyRegionCode) ||
    priceDisplayType === 'taxesAndFeesExclusive' ||
    isEnabledForFeature(config.TAX_EXCLUSIVE_REGIONS, currentRegionCode)

  return (
    <CurrencyProvider currency={currencyCode}>
      <PriceWrapper className={className}>
        <VerticalSpacer gap={4}>
          <OnlyIfChildren as={VerticalSpacer} gap={8}>
            {discountDisplayType === 'badge' && discount && (
              <NoPrint>
                <Pill kind="primary" variant="success">
                  Save up to {discount}%
                </Pill>
              </NoPrint>
            )}
            {depositComponent && <span>{depositComponent}</span>}
          </OnlyIfChildren>
          <PriceRowPriceCaption>{priceLabel}</PriceRowPriceCaption>
        </VerticalSpacer>
        <Group
          direction="horizontal"
          verticalAlign="center"
          gap={8}
          wrap="wrap"
        >
          <PriceRowPrice
            size="M"
            price={price}
            saleUnit={totalLabel ?? 'total'}
            taxesAndFees={
              priceDisplayType === 'taxesAndFeesInclusive'
                ? 'inclusive'
                : 'exclusive'
            }
          />
          {discountDisplayType === 'inlineBadge' && discount && (
            <NoPrint as="span">
              <Pill kind="primary" variant="success">
                Save up to {discount}%
              </Pill>
            </NoPrint>
          )}
        </Group>
        {showPriceDisplayType && (
          <>
            {priceDisplayType === 'taxesAndFeesInclusive' && (
              <Caption variant="large" colour="highlight-secondary">
                Includes taxes & fees
              </Caption>
            )}
            {priceDisplayType === 'taxesAndFeesExclusive' && (
              <Caption variant="large" colour="neutral-three">
                Taxes & fees apply
              </Caption>
            )}
            {priceDisplayType === 'feesExclusive' && (
              <Caption variant="large" colour="neutral-three">
                Fees apply
              </Caption>
            )}
          </>
        )}
        {discountDisplayType === 'following' && discount && (
          <NoPrint>
            <BodyText variant="small" weight="bold">
              {discount}% off best available rate
            </BodyText>
          </NoPrint>
        )}
        {roomOccupancy && <RoomText>{roomOccupancy}</RoomText>}
      </PriceWrapper>
    </CurrencyProvider>
  )
}
export default PriceDetails
